import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  GridLegacy as Grid,
  Typography,
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { Flag as FlagIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import { Challenge } from '../../types';
import { challengeService } from '../../services/challengeService';

interface CreateChallengeDialogProps {
  open: boolean;
  onClose: () => void;
  onChallengeCreated: (challenge: Challenge) => void;
  teamId: string;
}

const CreateChallengeDialog: React.FC<CreateChallengeDialogProps> = ({
  open,
  onClose,
  onChallengeCreated,
  teamId,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pointsReward: 1,
    type: 'daily' as 'daily' | 'weekly' | 'monthly',
    isActive: true,
    requirements: {
      activityType: '',
      minDistance: 0,
      minDuration: 0,
      consecutiveDays: 0,
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setFormData({
        title: '',
        description: '',
        pointsReward: 1,
        type: 'daily',
        isActive: true,
        requirements: {
          activityType: '',
          minDistance: 0,
          minDuration: 0,
          consecutiveDays: 0,
        },
      });
      setError('');
    }
  }, [open]);

  const handleInputChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRequirementsChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Challenge title is required');
      return;
    }

    if (!formData.description.trim()) {
      setError('Challenge description is required');
      return;
    }

    if (formData.pointsReward < 1 || formData.pointsReward > 5) {
      setError('Points reward must be between 1 and 5');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const challengeData = {
        teamId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        pointsReward: formData.pointsReward,
        type: formData.type,
        isActive: formData.isActive,
        startDate: Timestamp.now(), // Start immediately
        requirements: formData.requirements,
      };

      const challengeId = await challengeService.createChallenge(challengeData);
      
      const newChallenge: Challenge = {
        id: challengeId,
        ...challengeData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      onChallengeCreated(newChallenge);
      onClose();
    } catch (err: any) {
      console.error('Error creating challenge:', err);
      setError(err.message || 'Failed to create challenge');
    } finally {
      setLoading(false);
    }
  };

  const getChallengeTypeDescription = (type: string) => {
    switch (type) {
      case 'daily':
        return 'Challenge that can be completed daily';
      case 'weekly':
        return 'Challenge that can be completed weekly';
      case 'monthly':
        return 'Challenge that can be completed monthly';
      default:
        return '';
    }
  };

  const getPointsColor = (points: number) => {
    if (points <= 2) return 'success';
    if (points <= 3) return 'warning';
    return 'error';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon color="primary" />
          <Typography variant="h6">Create New Challenge</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Challenge Title"
              value={formData.title}
              onChange={handleInputChange('title')}
              placeholder="e.g., Daily 5K Run"
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Points Reward"
              type="number"
              value={formData.pointsReward}
              onChange={handleInputChange('pointsReward')}
              inputProps={{ min: 1, max: 5 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><TrophyIcon /></InputAdornment>,
              }}
              helperText="1-5 points"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={handleInputChange('description')}
              placeholder="Describe what athletes need to do to complete this challenge..."
              required
            />
          </Grid>

          {/* Challenge Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Challenge Type</InputLabel>
              <Select
                value={formData.type}
                onChange={handleInputChange('type')}
                label="Challenge Type"
              >
                <MenuItem value="daily">Daily Challenge</MenuItem>
                <MenuItem value="weekly">Weekly Challenge</MenuItem>
                <MenuItem value="monthly">Monthly Challenge</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {getChallengeTypeDescription(formData.type)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active Challenge"
              />
              <Chip 
                label={`${formData.pointsReward} points`} 
                color={getPointsColor(formData.pointsReward) as any}
                size="small"
              />
            </Box>
          </Grid>

          {/* Requirements */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Requirements (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set specific requirements for this challenge. Leave blank if not applicable.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={formData.requirements.activityType}
                onChange={handleRequirementsChange('activityType')}
                label="Activity Type"
              >
                <MenuItem value="">Any Activity</MenuItem>
                <MenuItem value="Run">Run</MenuItem>
                <MenuItem value="Workout">Run — workout (structured miles)</MenuItem>
                <MenuItem value="Strength">Strength</MenuItem>
                <MenuItem value="Cross Training">Cross Training</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Minimum Distance (miles)"
              type="number"
              value={formData.requirements.minDistance}
              onChange={handleRequirementsChange('minDistance')}
              inputProps={{ min: 0, step: 0.1 }}
              placeholder="0 for no minimum"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Minimum Duration (minutes)"
              type="number"
              value={formData.requirements.minDuration}
              onChange={handleRequirementsChange('minDuration')}
              inputProps={{ min: 0 }}
              placeholder="0 for no minimum"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Consecutive Days Required"
              type="number"
              value={formData.requirements.consecutiveDays}
              onChange={handleRequirementsChange('consecutiveDays')}
              inputProps={{ min: 0 }}
              placeholder="0 for no streak requirement"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title.trim() || !formData.description.trim()}
          startIcon={<FlagIcon />}
        >
          {loading ? 'Creating...' : 'Create Challenge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateChallengeDialog;
